'use client'

import Error from '@/components/common/error'
import ArrowLeft from '@/components/icons/arrow-left'
import ArrowRight from '@/components/icons/arrow-right'
import GamepadInSquare from '@/components/icons/gamepad-in-square'
import { Button } from '@/components/ui/button'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@/components/ui/carousel'
import Autoplay from 'embla-carousel-autoplay'
import Image from 'next/image'
import Link from 'next/link'
import { useEffect, useMemo, useState } from 'react'

export default function PickYourGame({ games = [] }) {
  const viewAllGamesHref = '/sim-racing-games'
  const [carouselApi, setCarouselApi] = useState(null)
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)

  const sortedGames = useMemo(() => {
    if (!Array.isArray(games)) return []
    return [...games].sort((a, b) => {
      if (a.featured && !b.featured) return -1
      if (!a.featured && b.featured) return 1
      return a.nicename.localeCompare(b.nicename)
    })
  }, [games])

  useEffect(() => {
    if (!carouselApi) return

    const updateScrollState = () => {
      setCanScrollPrev(carouselApi.canScrollPrev())
      setCanScrollNext(carouselApi.canScrollNext())
    }

    updateScrollState()
    carouselApi.on('select', updateScrollState)
    carouselApi.on('reInit', updateScrollState)

    return () => {
      carouselApi.off('select', updateScrollState)
      carouselApi.off('reInit', updateScrollState)
    }
  }, [carouselApi])

  return (
    <div className="mt-[72px] overflow-hidden">
      <div className="mx-auto flex flex-col items-center justify-center gap-4 font-inter">
        <GamepadInSquare className="h-12 w-12 stroke-white" />
        <h3 className="select-none px-3 text-center text-h-3 font-semibold">
          Pick your game
        </h3>
        <p className="max-w-[550px] select-none px-3 text-center text-p-3 font-normal text-neutral-blue-500">
          Search communities and events from over 40 different games. From
          Trackmania to iRacing and everything in between.
        </p>
        <Button
          variant="outline"
          asChild
          className="hover:border-shades-0! hover:bg-shades-0! hidden transition-all duration-300 ease-in-out desktop-xs:block"
        >
          <Link
            className="hover:text-black! transition-all ease-in-out"
            href={viewAllGamesHref}
            prefetch={false}
          >
            View all
          </Link>
        </Button>
      </div>

      <div className="screen-grid mt-8">
        {!sortedGames?.length ? (
          <Error
            icon="exclamation"
            size="hero"
            className="mx-auto my-16 w-full max-w-7xl bg-red-600 text-white"
            error={{
              code: 404,
              message: `Unable to load games".`,
            }}
          />
        ) : (
          <div className="tablet:grid-indent relative col-span-full min-w-0">
            <div className="mx-[-24px] tablet:mx-0">
              {canScrollPrev && (
                <button
                  type="button"
                  onClick={() => carouselApi?.scrollPrev()}
                  aria-label="Previous games"
                  className="carousel-btn-bg group absolute -left-20 top-1/2 z-10 hidden -translate-y-1/2 cursor-pointer flex-col justify-center rounded-sm bg-neutral-blue-900 transition-all desktop-xs:flex desktop-lg:w-[109px]"
                >
                  <div className="mx-auto flex size-12 items-center justify-center rounded-full border border-neutral-blue-600 bg-neutral-blue-700 group-hover:bg-neutral-blue-600">
                    <ArrowLeft className="m-auto h-4 w-4 stroke-white stroke-2" />
                  </div>
                </button>
              )}

              <Carousel
                setApi={setCarouselApi}
                opts={{ align: 'start', slidesToScroll: 'auto' }}
                plugins={[
                  Autoplay({
                    delay: 8000,
                    stopOnMouseEnter: true,
                    stopOnInteraction: false,
                  }),
                ]}
                className="w-full min-w-0"
              >
                <CarouselContent>
                  {sortedGames.map((game) => (
                    <CarouselItem
                      key={game.name}
                      className="basis-[calc(100%-24px)] first:pl-6 last:pr-6 mobile:basis-1/2 tablet:basis-1/4 desktop-xs:basis-1/6 desktop-xs:first:pl-4 desktop-xs:last:pr-0"
                    >
                      <Link
                        href={`/sim-racing-games/${game.name}`}
                        className="group"
                        prefetch={false}
                      >
                        <div className="rounded-sm border-2 border-neutral-blue-600 transition-all duration-300 ease-in-out group-hover:border-neutral-blue-500">
                          <Image
                            alt={game.name}
                            height={800}
                            width={800}
                            sizes="(min-width: 1280px) 200px, (min-width: 1024px) calc((100vw - 64px) / 6), (min-width: 768px) calc((100vw - 64px) / 4), (min-width: 640px) calc((100vw - 48px) / 2), calc(100vw - 48px)"
                            className="aspect-2/3 rounded-[3px] object-cover"
                            src={
                              game.cardImgUrl ||
                              '/img/default/standalone-header.webp'
                            }
                          />
                        </div>
                        <p className="mt-3 text-p-2 font-semibold underline decoration-transparent underline-offset-2 transition-all duration-300 ease-in-out group-hover:decoration-white desktop-xs:text-p-3">
                          {game.longname}
                        </p>
                      </Link>
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>
            </div>

            {canScrollNext && (
              <button
                type="button"
                onClick={() => carouselApi?.scrollNext()}
                aria-label="Next games"
                className="carousel-btn-bg group absolute -right-20 top-1/2 z-10 hidden -translate-y-1/2 cursor-pointer flex-col justify-center rounded-sm bg-neutral-blue-900 transition-all desktop-xs:flex desktop-lg:w-[109px]"
              >
                <div className="mx-auto flex size-12 items-center justify-center rounded-full border border-neutral-blue-600 bg-neutral-blue-700 group-hover:bg-neutral-blue-600">
                  <ArrowRight className="m-auto h-4 w-4 stroke-white stroke-2" />
                </div>
              </button>
            )}
          </div>
        )}
      </div>

      <div className="screen-grid *:grid-indent mt-4 grid desktop-xs:mt-[34px] desktop-xs:hidden">
        <Button
          variant="outline"
          asChild
          className="scroll-py-3! px-4! hover:border-shades-0! hover:bg-white! w-full text-center transition-all duration-300 ease-in-out"
        >
          <Link
            href={viewAllGamesHref}
            className="hover:text-black! transition-all ease-in-out"
            prefetch={false}
          >
            View all
          </Link>
        </Button>
      </div>
    </div>
  )
}
